// ELVA's work fixtures (ELVA_WORK_HANDOFF §7). Copy is from the boards.
import type { AppealsSummary, MoneyItem, Period, Report, ReportRow } from './types';
import type { LogDay, LogEntry } from './types';
import { money } from './utils';
import { getSentSync } from '@/features/appeals/store';
import { PMS } from '@/features/shared/pms';

/**
 * The Appeals card figures, read from the ONE source the Denied & underpaid ›
 * Appeals sent page uses (rcm-03 Fix-on-staging: "One source for both"). Counts
 * and amounts here are the same records that page shows, so the two never
 * diverge. Amounts are the claim amounts under appeal (D156: the expected
 * insurer payment is what Denied & underpaid shows; both pages share it).
 */
function liveAppealFigures(): AppealsSummary['figures'] {
  const sent = getSentSync();
  const sentSum = sent.reduce((s, r) => s + r.billed, 0);
  const waiting = sent.filter((r) => r.status === 'waiting' || r.status === 'faxed' || r.status === 'sent');
  const waitingSum = waiting.reduce((s, r) => s + r.billed, 0);
  const won = sent.filter((r) => typeof r.recoveredAmount === 'number');
  const wonSum = won.reduce((s, r) => s + (r.recoveredAmount ?? 0), 0);
  const firstWon = won[0];
  return [
    { label: 'Sent', value: `${String(sent.length)} · ${money(sentSum)}`, meta: 'the same appeals as Denied & underpaid › Appeals sent' },
    { label: 'Won back', value: money(wonSum), meta: firstWon ? `${firstWon.payerShort} paid ${firstWon.patient}’s appeal` : 'none paid yet' },
    { label: 'Waiting for an answer', value: `${String(waiting.length)} · ${money(waitingSum)}`, meta: 'ELVA follows each one up' },
    { label: 'Deadlines missed', value: 'None', meta: 'the closest is Mar 24, 2027' },
  ];
}

const OCT_APPEALS: AppealsSummary = {
  openHref: '/denied',
  figures: liveAppealFigures(),
  did: [
    { text: 'Read 8 denials and short payments and chose what to do: 3 appeals, 2 reprocess, 1 resend, 1 bill the patient, 1 asked you.', link: { label: 'See in log', href: '/elva-work/log?kind=appeals' } },
    { text: `Wrote 6 letters and built 6 packets from ${PMS}, the EOBs and the payers’ own forms.` },
    { text: 'Got Dr. Carter’s approval of Linda Okafor’s clinical letter by text, Oct 14.' },
    { text: 'Got Helen Ortiz to sign Aetna’s form on her phone, the day after ELVA texted it.' },
    { text: 'Asked Delta for a claim adjustment first, as Delta requires, before Priya Shah’s dispute.' },
    { text: 'Called Aetna to have James Whitfield’s crown reprocessed · R-26-55810.', link: { label: 'Hear the call', href: '/elva-work/log/log-call-aetna' } },
  ],
  channels: [
    { label: 'Payer websites · 2', detail: 'Linda Okafor · Aetna · Priya Shah · Delta', badge: 'Both confirmed', badgeCls: 'ok' },
    { label: 'Fax · 2', detail: 'Helen Ortiz · Aetna · Daniel Kim · Guardian', badge: '1 not confirmed yet', badgeCls: 'warn' },
    { label: 'Secure email · 1', detail: 'Robert Nguyen · Humana, a reconsideration', badge: 'Confirmed', badgeCls: 'ok' },
    { label: 'Certified mail · 1', detail: 'Michael Torres · Cigna, the only way Cigna takes appeals', badge: 'Being printed', badgeCls: 'mute' },
  ],
  channelFooter: 'The payer confirmed it has 4 of the 6. ELVA calls Guardian tomorrow about Daniel Kim’s fax.',
  decision: { text: 'Needs your decision: Aetna kept its denial of Rachel Stone’s build-up ($285).', link: { label: 'Decide in Appeals sent', href: '/denied/appeals/CLM-0611' } },
};

interface RawPeriod {
  rows: ReportRow[];
  money: MoneyItem[];
  appeals: AppealsSummary | null;
  corrections: Report['corrections'];
  onItsOwn: number;
  confirmed: number;
  changed: number;
  undone: number;
}

const PERIOD_LABELS: Record<Period, string> = {
  '2026-10': 'October 2026, so far',
  '2026-09': 'September 2026',
  since: 'Since ELVA started',
};

// D156: every denied/short-paid figure counts the EXPECTED insurer payment, not
// the billed fee. The billed fee is only a note at the end of the sub-line.
const RAW: Record<Period, RawPeriod> = {
  '2026-10': {
    rows: [
      { kind: 'claims_sent', task: 'Claims prepared and sent', count: 47, countedAs: '10 min each', hours: 7.8 },
      { kind: 'calls', task: 'Calls to payers', count: 12, countedAs: 'Real length, hold included', hours: 6.1, money: 842.0, subNote: '3.4 h of it on hold' },
      { kind: 'appeals', task: 'Appeals sent', count: 6, countedAs: '45 min each', hours: 4.5, subNote: '2 on payer websites, 2 by fax, 1 by secure email, 1 by certified mail' },
      { kind: 'posted', task: 'Insurance payments posted', count: 38, countedAs: '5 min each', hours: 3.2 },
      { kind: 'eob_claims', task: 'Claims posted from paper EOBs', count: 18, countedAs: '8 min each', hours: 2.4 },
      { kind: 'portal', task: 'Payer portal visits', count: 29, countedAs: '4 min each', hours: 1.9, money: 1312.4 },
      { kind: 'denials', task: 'Denials and underpayments found', count: 8, countedAs: '10 min each', hours: 1.3, money: 3481.0 },
      { kind: 'preauths', task: 'Pre-auths prepared and sent', count: 4, countedAs: '15 min each', hours: 1.0 },
      { kind: 'bank_diffs', task: 'Bank deposit differences found', count: 4, countedAs: '15 min each', hours: 1.0, money: 4.18 },
    ],
    money: [
      { what: '5 denied claims', note: '2 appeals sent, 1 claim resent, 1 billed to the patient, 1 waiting for Sofia’s answer. Billed $3,983.00.', amount: 3145.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: '3 underpaid claims', note: 'Aetna is reprocessing James Whitfield’s crown, ref R-26-55810', amount: 336.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: 'A Delta deposit nobody had posted', note: 'ELVA got the remittance from Delta’s portal', amount: 1312.4, status: 'paid' },
      { what: 'A Cigna payment sent to account ··9021', note: 'Is ··9021 your account? Answer in Posting', amount: 842.0, status: 'waiting', link: { label: 'Answer in Posting', href: '/posting/bank' } },
      { what: 'Interest Aetna added for paying late', note: 'On the deposit of Oct 7', amount: 4.18, status: 'paid' },
    ],
    appeals: OCT_APPEALS,
    corrections: [
      { id: 'cor-1', label: 'Undo', text: 'Ben Carter’s posting of Oct 9 went to the wrong visit. Undone by Ava Thompson, Oct 15.' },
      { id: 'cor-2', label: 'Change', text: 'Maria Lopez’s claim: tooth number changed from #3 to #14 by Ava Thompson, Oct 13.' },
      { id: 'cor-3', label: 'Change', text: 'Jorge Martinez’s payment: Ava confirmed which claim it was, Oct 15.' },
    ],
    onItsOwn: 53,
    confirmed: 113,
    changed: 6,
    undone: 1,
  },
  '2026-09': {
    rows: [
      { kind: 'claims_sent', task: 'Claims prepared and sent', count: 58, countedAs: '10 min each', hours: 9.7 },
      { kind: 'calls', task: 'Calls to payers', count: 15, countedAs: 'Real length, hold included', hours: 7.2, subNote: '4.1 h of it on hold' },
      { kind: 'appeals', task: 'Appeals sent', count: 4, countedAs: '45 min each', hours: 3.0, subNote: '2 by fax, 1 by certified mail, 1 on a payer website' },
      { kind: 'posted', task: 'Insurance payments posted', count: 52, countedAs: '5 min each', hours: 4.3 },
      { kind: 'eob_claims', task: 'Claims posted from paper EOBs', count: 22, countedAs: '8 min each', hours: 2.9 },
      { kind: 'portal', task: 'Payer portal visits', count: 34, countedAs: '4 min each', hours: 2.3, money: 940.0 },
      { kind: 'denials', task: 'Denials and underpayments found', count: 11, countedAs: '10 min each', hours: 1.8, money: 2680.0 },
      { kind: 'preauths', task: 'Pre-auths prepared and sent', count: 6, countedAs: '15 min each', hours: 1.5 },
      { kind: 'bank_diffs', task: 'Bank deposit differences found', count: 2, countedAs: '15 min each', hours: 0.5 },
    ],
    money: [
      { what: '4 denied claims', note: '2 appeals sent, 1 reprocessed, 1 billed to the patient. Billed $2,540.00.', amount: 2100.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: '2 underpaid claims', note: 'Both reprocessed at the network fee', amount: 580.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: 'A Humana deposit nobody had posted', note: 'ELVA got the remittance from Humana’s portal', amount: 940.0, status: 'paid' },
    ],
    appeals: null,
    corrections: [
      { id: 'cor-s1', label: 'Change', text: 'Nina Alvarez’s claim: date of service corrected by Ava Thompson, Sep 24.' },
      { id: 'cor-s2', label: 'Undo', text: 'A Guardian posting went to the wrong patient. Undone by Ryan Cole, Sep 18.' },
    ],
    onItsOwn: 78,
    confirmed: 126,
    changed: 9,
    undone: 2,
  },
  since: {
    rows: [
      { kind: 'claims_sent', task: 'Claims prepared and sent', count: 612, countedAs: '10 min each', hours: 102.0 },
      { kind: 'calls', task: 'Calls to payers', count: 138, countedAs: 'Real length, hold included', hours: 71.4, subNote: '38.6 h of it on hold' },
      { kind: 'appeals', task: 'Appeals sent', count: 41, countedAs: '45 min each', hours: 30.8, subNote: 'across payer websites, fax, secure email and certified mail' },
      { kind: 'posted', task: 'Insurance payments posted', count: 503, countedAs: '5 min each', hours: 41.9 },
      { kind: 'eob_claims', task: 'Claims posted from paper EOBs', count: 196, countedAs: '8 min each', hours: 26.1 },
      { kind: 'portal', task: 'Payer portal visits', count: 388, countedAs: '4 min each', hours: 25.9, money: 14230.5 },
      { kind: 'denials', task: 'Denials and underpayments found', count: 92, countedAs: '10 min each', hours: 15.3, money: 34570.0 },
      { kind: 'preauths', task: 'Pre-auths prepared and sent', count: 48, countedAs: '15 min each', hours: 12.0 },
      { kind: 'bank_diffs', task: 'Bank deposit differences found', count: 22, countedAs: '15 min each', hours: 5.5, money: 92.4 },
    ],
    money: [
      { what: '46 denied claims', note: 'Appealed, reprocessed, resent or billed to the patient. Billed $35,900.00.', amount: 28450.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: '31 underpaid claims', note: 'Reprocessed at the contracted fee', amount: 6120.0, status: 'collecting', link: { label: 'Denied & underpaid', href: '/denied' } },
      { what: 'Deposits nobody had posted', note: 'Remittances ELVA pulled from payer portals', amount: 14230.5, status: 'paid' },
      { what: 'Interest payers added for paying late', note: 'Across the deposits since go-live', amount: 92.4, status: 'paid' },
    ],
    appeals: { ...OCT_APPEALS, figures: liveAppealFigures() },
    corrections: [
      { id: 'cor-h1', label: 'Change', text: 'Across the period, your team changed 51 things ELVA confirmed and undid 7. ELVA learned from each.' },
    ],
    onItsOwn: 734,
    confirmed: 1306,
    changed: 51,
    undone: 7,
  },
};

/**
 * Build the whole report for a period. Every figure, row and section comes from
 * the period's own data, and the headline/figure totals are derived from the
 * rows + money so the select re-queries everything, not just the headline
 * (rcm-03 Fix-on-staging).
 */
export function buildReport(period: Period = '2026-10'): Report {
  const raw = RAW[period];
  const hours = Math.round(raw.rows.reduce((s, r) => s + r.hours, 0) * 10) / 10;
  const tasks = raw.rows.reduce((s, r) => s + r.count, 0);
  const moneyFound = raw.money.reduce((s, m) => s + m.amount, 0);
  const moneyPaid = raw.money.filter((m) => m.status === 'paid').reduce((s, m) => s + m.amount, 0);
  const moneyCollecting = moneyFound - moneyPaid;
  return {
    period,
    periodLabel: PERIOD_LABELS[period],
    hours,
    tasks,
    onItsOwn: raw.onItsOwn,
    confirmed: raw.confirmed,
    changed: raw.changed,
    undone: raw.undone,
    moneyFound,
    moneyPaid,
    moneyCollecting,
    rows: raw.rows,
    appeals: raw.appeals,
    money: raw.money,
    corrections: raw.corrections,
  };
}

// ---- log ----

const AETNA_CALL: LogEntry = {
  id: 'log-call-aetna',
  time: '9:58 AM',
  day: 'Today, Oct 15',
  kind: 'call',
  kindLabel: 'Call',
  what: 'Asked Aetna to reprocess James Whitfield’s crown',
  meta: 'Aetna · claim of Sep 9',
  how: 'Call · 11 min',
  result: 'Aetna will reprocess · R-26-55810',
  by: 'ELVA, after Ava Thompson chose Reprocess',
  doneBy: 'confirmed',
  title: 'Call to Aetna dental provider services',
  headerMeta: 'Today 9:58 AM · 11 min 24 s, 8 min 15 s of it on hold',
  why: 'Ava Thompson (Lone Star Billing) chose Reprocess for James Whitfield’s crown on Denied & underpaid. ELVA called right away.',
  whyLink: { label: 'Open the claim', href: '/denied/CLM-0909' },
  ticks: [
    { text: 'Aetna will reprocess the claim at Dr. Carter’s network fee.' },
    { text: 'Reference R-26-55810, given by Derek.' },
    { text: 'Within 5 business days, paid by EFT. ELVA checks Aetna’s portal on Oct 22.' },
  ],
  durationSec: 684,
  holdSec: 495,
  transcript: [
    { time: '0:00', speaker: 'Aetna, recorded', text: 'Thank you for calling Aetna Dental. This call may be monitored or recorded.' },
    { time: '0:06', speaker: 'ELVA', text: 'ELVA chose Provider, then Claims, and entered the tax ID and claim number.', system: true },
    { time: '0:41', speaker: '', text: 'On hold, 6 min 10 s.', system: true },
    { time: '6:51', speaker: 'Derek, Aetna', text: 'Aetna dental provider services, this is Derek. Who am I speaking with?' },
    { time: '6:55', speaker: 'ELVA', text: 'Hi Derek, this is ELVA, an automated assistant calling for Alamo Heights Dental. This call is recorded. I’m calling about a claim that was paid at the wrong fee.' },
    { time: '7:05', speaker: 'Derek, Aetna', text: 'Okay. Can I have the tax ID and the claim number?' },
    { time: '7:09', speaker: 'ELVA', text: 'Tax ID 74-2918830. Claim EH4471029, for James Whitfield, member W281-77-1904, date of service September 9, 2026.' },
    { time: '7:24', speaker: 'Derek, Aetna', text: 'I have it. Crown on tooth 14, paid $408 on October 2.' },
    { time: '7:30', speaker: 'ELVA', text: 'That’s the one. It was paid at the out-of-network fee of $680. The treating dentist, Dr. Emily Carter, has been in Aetna’s network since March 2019, so it should use the network fee of $1,020. Could you reprocess it?' },
    { time: '7:48', speaker: 'Derek, Aetna', text: 'Let me check her status. One moment.' },
    { time: '7:52', speaker: '', text: 'On hold, 2 min 05 s.', system: true },
    { time: '9:57', speaker: 'Derek, Aetna', text: 'Thanks for holding. She’s participating. The claim went through without the network flag. I’m sending it back for reprocessing.' },
    { time: '10:40', speaker: 'ELVA', text: 'Thank you. Could I have a reference number?' },
    { time: '10:44', speaker: 'Derek, Aetna', text: 'R-26-55810. You should see it within 5 business days, paid by EFT.' },
    { time: '11:05', speaker: 'ELVA', text: 'R-26-55810, within 5 business days. Thank you, Derek.' },
    { time: '11:24', speaker: '', text: 'Call ended.', system: true },
  ],
};

// The call ELVA made after MetLife's portal password expired (rcm-03
// Fix-on-staging: "See the call" must open THIS entry, not the Aetna call).
const METLIFE_CALL: LogEntry = {
  id: 'log-call-metlife',
  time: '7:41 AM',
  day: 'Today, Oct 15',
  kind: 'call',
  kindLabel: 'Call',
  what: 'Called MetLife for 2 claims the portal was blocking',
  meta: 'MetLife · 2 claims',
  how: 'Call · 9 min',
  result: 'Got both statuses',
  by: 'ELVA on its own',
  doneBy: 'own',
  title: 'Call to MetLife dental provider services',
  headerMeta: 'Today 7:41 AM · 9 min 02 s, 5 min 40 s of it on hold · ELVA on its own',
  why: 'MetLife’s portal password had expired, so ELVA called instead to read the 2 claim statuses it needed.',
  ticks: [
    { text: 'Claim of Sep 28 · paid Oct 10, EFT. ELVA will match it when the remittance arrives.' },
    { text: 'Claim of Oct 2 · in process, MetLife expects to finish by Oct 20.' },
  ],
  durationSec: 542,
  holdSec: 340,
  transcript: [
    { time: '0:00', speaker: 'MetLife, recorded', text: 'Thank you for calling MetLife Dental. This call may be recorded.' },
    { time: '0:05', speaker: 'ELVA', text: 'ELVA chose Provider, then Claim status, and entered the tax ID.', system: true },
    { time: '0:30', speaker: '', text: 'On hold, 5 min 40 s.', system: true },
    { time: '6:10', speaker: 'Rosa, MetLife', text: 'MetLife provider services, this is Rosa. Who am I speaking with?' },
    { time: '6:14', speaker: 'ELVA', text: 'Hi Rosa, this is ELVA, an automated assistant calling for Alamo Heights Dental. This call is recorded. I have two claims to check.' },
    { time: '8:55', speaker: '', text: 'Call ended.', system: true },
  ],
};

const TODAY: LogEntry[] = [
  {
    id: 'log-post-jorge',
    time: '10:42 AM', day: 'Today, Oct 15', kind: 'posted', kindLabel: 'Posted',
    what: 'Posted $486.00 on Jorge Martinez’s claim of Sep 22', meta: 'Delta Dental of Texas',
    how: `ELVA’s agent in ${PMS}`, result: 'Posted and checked', by: 'Confirmed by Ava Thompson · Lone Star Billing', doneBy: 'confirmed',
    title: 'Posted $486.00 on Jorge Martinez’s claim of Sep 22',
    headerMeta: `ELVA’s agent in ${PMS} · today 10:42 AM · 23 s · confirmed by Ava Thompson (Lone Star Billing)`,
    steps: [
      { time: '10:41:58', text: 'Opened Office Manager › Batch Processor and the Delta Dental ERA of Oct 8' },
      { time: '10:42:04', text: 'Chose Select Claim on the line J MARTINEZ · $486.00 (marked Not Found)' },
      { time: '10:42:09', text: 'Picked Jorge Martinez · Sep 22, 2026 · $1,180.00' },
      { time: '10:42:13', text: 'Entered paid $486.00 and write-off $208.00 on D2740 #3' },
      { time: '10:42:16', text: 'Posted' },
      { time: '10:42:21', text: 'Read it back: the claim shows Received; Jorge’s balance is $486.00' },
    ],
    openLink: { label: 'Open in Posting › Posted', href: '/posting/posted/PD-JORGE' },
  },
  {
    id: 'log-team-jorge', time: '10:41 AM', day: 'Today, Oct 15', kind: 'your_team', kindLabel: 'Your team',
    what: 'Confirmed which claim Jorge Martinez’s payment was for', meta: 'Posting', how: 'ELVA', result: 'Done', by: 'Ava Thompson · Lone Star Billing', doneBy: 'person',
    title: 'Confirmed which claim Jorge Martinez’s payment was for',
    headerMeta: 'Posting · today 10:41 AM · Ava Thompson (Lone Star Billing)',
    openLink: { label: 'Open in Posting › Posted', href: '/posting/posted/PD-JORGE' },
  },
  {
    id: 'log-appeal-linda', time: '10:30 AM', day: 'Today, Oct 15', kind: 'appeal', kindLabel: 'Appeal',
    what: 'Filed Linda Okafor’s appeal on Aetna’s website', meta: 'Aetna · $1,580 · clinical, approved by Dr. Carter',
    how: 'Aetna dental website', result: 'Aetna confirmed · AD-7731904', by: 'ELVA, after Ava Thompson pressed Send', doneBy: 'confirmed',
    title: 'Filed Linda Okafor’s appeal on Aetna’s website',
    headerMeta: 'Aetna dental website · today 10:30 AM · 1 min 52 s · ELVA, after Ava Thompson (Lone Star Billing) pressed Send',
    why: 'Aetna denied the crown and build-up as not necessary. Clinical appeal: Dr. Carter approved the letter by text on Oct 14.',
    whyLink: { label: 'Open the appeal', href: '/denied/appeals/CLM-0818' },
    steps: [
      { time: '10:30:02', text: 'Signed in to Aetna’s dental provider website as alamoheights-billing' },
      { time: '10:30:21', text: 'Opened the EOB tool and found claim CLM-0818, denied Oct 1' },
      { time: '10:30:35', text: 'Chose Dispute this claim · Clinical' },
      { time: '10:31:02', text: 'Uploaded the packet: 1 PDF, 9 pages (letter, Aetna form, EOB, claim, X-ray, clinical note)' },
      { time: '10:31:30', text: 'Entered the reason from the letter’s first paragraph and submitted' },
      { time: '10:31:48', text: 'Aetna showed confirmation AD-7731904' },
      { time: '10:31:54', text: 'Signed out' },
    ],
    ticks: [{ text: 'Aetna has it. Aetna answers within 60 days, by Dec 14. ELVA watches Aetna’s website and payments for the answer.' }],
    sources: ['The packet as sent (PDF, 9 pages)', 'Aetna’s confirmation (text)'],
  },
  {
    id: 'log-appeal-michael', time: '10:24 AM', day: 'Today, Oct 15', kind: 'appeal', kindLabel: 'Appeal',
    what: 'Sent Michael Torres’s appeal by certified mail', meta: 'Cigna · $1,295 · 8 pages',
    how: 'Mail service', result: 'Being printed', by: 'ELVA, after Ava Thompson pressed Send', doneBy: 'confirmed',
    title: 'Sent Michael Torres’s appeal by certified mail',
    headerMeta: 'Mail service · today 10:24 AM · ELVA, after Ava Thompson (Lone Star Billing) pressed Send',
    why: 'Cigna denied the crown. Billing appeal, signed for the practice. Certified mail is the only way Cigna takes appeals.',
    whyLink: { label: 'Open the appeal', href: '/denied/appeals/CLM-0827' },
    ticks: [{ text: 'The letter and 8-page packet are printing now. ELVA mails it certified with return receipt and watches for the receipt.' }],
  },
  AETNA_CALL,
  METLIFE_CALL,
  {
    id: 'log-doc-delta', time: '9:12 AM', day: 'Today, Oct 15', kind: 'document', kindLabel: 'Document',
    what: 'Read a faxed EOB: check 004417820, 9 claims', meta: 'Delta Dental of Texas · $3,486.20', how: 'Fax', result: '8 ready, 1 needs you', by: 'ELVA on its own', doneBy: 'own',
    title: 'Read a faxed EOB: Delta Dental of Texas, check 004417820',
    headerMeta: 'Fax · 4 pages · today 9:12 AM · ELVA on its own',
    ticks: [{ text: 'Found 9 claims totalling $3,486.20. 8 are ready; 1 needs a person.' }],
    openLink: { label: 'Open the check in Document Center › Checks', href: '/document-center/checks' },
    fileLink: 'The faxed EOB (PDF, 4 pages)',
  },
  {
    id: 'log-portal-guardian', time: '8:52 AM', day: 'Today, Oct 15', kind: 'portal', kindLabel: 'Portal',
    what: 'Downloaded the EOB for check 77120458', meta: 'Guardian · the missing page 3', how: 'Guardian provider portal', result: 'Got the full EOB', by: 'ELVA on its own', doneBy: 'own',
    title: 'Downloaded the EOB for check 77120458',
    headerMeta: 'Guardian provider portal · today 8:52 AM · 41 s · ELVA on its own',
    why: 'the faxed EOB was missing page 3 of 4.', whyLink: { label: 'Open the check in Document Center › Checks', href: '/document-center/checks' },
    steps: [
      { time: '8:52:03', text: 'Signed in to Guardian’s provider portal as alamoheights-billing' },
      { time: '8:52:20', text: 'Searched payments for check 77120458' },
      { time: '8:52:31', text: 'Opened the EOB, 4 pages, dated Oct 9' },
      { time: '8:52:40', text: 'Downloaded the EOB' },
      { time: '8:52:44', text: 'Signed out' },
    ],
    ticks: [{ text: 'Page 3 holds one claim: Nina Hale, Sep 25, $316.40. Pages 1, 2 and 4 match the fax line for line.' }],
    fileLink: 'EOB from the portal (PDF, 4 pages)',
  },
  {
    id: 'log-doc-guardian', time: '8:40 AM', day: 'Today, Oct 15', kind: 'document', kindLabel: 'Document',
    what: 'Read a faxed EOB: check 77120458, page 3 missing', meta: 'Guardian · $1,284.50', how: 'Fax', result: '3 of 4 pages', by: 'ELVA on its own', doneBy: 'own',
    title: 'Read a faxed EOB: Guardian, check 77120458',
    headerMeta: 'Fax · 3 pages · today 8:40 AM · ELVA on its own',
    ticks: [{ text: 'The fax had pages 1, 2 and 4. ELVA fetched page 3 from Guardian’s portal.' }],
    openLink: { label: 'Open the check in Document Center › Checks', href: '/document-center/checks' },
  },
  {
    id: 'log-portal-metlife', time: '7:30 AM', day: 'Today, Oct 15', kind: 'portal', kindLabel: 'Portal',
    what: 'Tried to sign in to MetLife’s portal', meta: 'MetLife', how: 'MetLife provider portal', result: 'Password expired', resultBad: true, by: 'ELVA on its own', doneBy: 'own',
    title: 'Tried to sign in to MetLife’s portal',
    headerMeta: 'MetLife provider portal · today 7:30 AM · ELVA on its own',
    failText: 'MetLife says the password has expired',
    ticks: [
      { text: '7:41 AM · ELVA called MetLife instead for the 2 claims it needed.', link: { label: 'See the call', href: '/elva-work/log/log-call-metlife' } },
      { text: '7:42 AM · Asked Ava Thompson to update the password.' },
    ],
    fixButton: { label: 'Settings › Payer portals', href: '/settings/portals' },
  },
  {
    id: 'log-claims-6', time: '7:02 AM', day: 'Today, Oct 15', kind: 'claims', kindLabel: 'Claims',
    what: 'Sent 6 claims', meta: '6 patients · Delta, Cigna, MetLife', how: `ELVA’s agent in ${PMS}`, result: 'All 6 accepted', by: 'Confirmed by Ava Thompson · Lone Star Billing', doneBy: 'confirmed',
    title: 'Sent 6 claims',
    headerMeta: `ELVA’s agent in ${PMS} · today 7:02 AM · confirmed by Ava Thompson (Lone Star Billing)`,
    ticks: [{ text: 'All 6 claims were accepted by the clearinghouse.' }],
    claimRows: [
      { patient: 'Grace Howard', payer: 'Delta Dental of Texas', amount: '$1,284.00', response: 'Accepted · ack 0X11-5521' },
      { patient: 'Owen Fischer', payer: 'Delta Dental of Texas', amount: '$164.00', response: 'Accepted · ack 0X11-5522' },
      { patient: 'Aisha Patel', payer: 'Cigna', amount: '$210.00', response: 'Accepted · ack CG-88107' },
      { patient: 'Michael Torres', payer: 'Cigna', amount: '$1,295.00', response: 'Accepted · ack CG-88108' },
      { patient: 'Noah Kim', payer: 'MetLife', amount: '$342.00', response: 'Accepted · ack ML-40192' },
      { patient: 'Sana Malik', payer: 'MetLife', amount: '$766.00', response: 'Accepted · ack ML-40193' },
    ],
  },
];

const SOFIA_API: LogEntry = {
  id: 'log-post-sofia', time: '10:15 AM', day: 'Yesterday, Oct 14', kind: 'posted', kindLabel: 'Posted',
  what: 'Posted $164.00 on Sofia Romero’s claim of Oct 6', meta: 'Delta Dental of Texas', how: `Through the ${PMS} API`, result: 'Posted and checked', by: 'Confirmed by Ava Thompson · Lone Star Billing', doneBy: 'confirmed',
  title: 'Posted $164.00 on Sofia Romero’s claim of Oct 6',
  headerMeta: `Through the ${PMS} API · today 10:15 AM · confirmed by Ava Thompson (Lone Star Billing)`,
  changes: [
    { record: 'Claim 44902 status', before: 'Sent', after: 'Received' },
    { record: 'Insurance payment, Delta Dental of Texas', before: '—', after: '$164.00' },
    { record: 'Write-off on D1110 and D0120', before: '—', after: '$72.00' },
    { record: 'Sofia Romero’s balance', before: '$0.00', after: '$0.00' },
  ],
  changesNote: `Each change carries its ${PMS} record number, for your audit.`,
};

const PERSON_ACTION: LogEntry = {
  id: 'log-team-cigna', time: '3:20 PM', day: 'Yesterday, Oct 14', kind: 'your_team', kindLabel: 'Your team',
  what: 'Turned off Cigna · implants in Settings › Pre-auths', meta: 'Settings › Pre-auths', how: 'ELVA', result: 'Saved', by: 'Ava Thompson · Lone Star Billing', doneBy: 'person',
  title: 'Turned off Cigna · implants in Settings › Pre-auths',
  headerMeta: 'Ava Thompson · Lone Star Billing · Oct 13, 3:20 PM',
  personChanges: [{ record: 'Prepare pre-auths for Cigna implants', before: 'On', after: 'Off' }],
  personWhere: 'Settings › Pre-auths',
  personReason: 'From the Don’t send dialog on Daniel Foster’s pre-auth. Reason given: Daniel will pay for it himself.',
};

export function buildLog(): LogDay[] {
  return [
    { date: 'Today, Oct 15', total: 10, entries: TODAY },
    { date: 'Yesterday, Oct 14', total: 41, entries: [SOFIA_API, PERSON_ACTION] },
  ];
}

export function allEntries(): LogEntry[] {
  return buildLog().flatMap((d) => d.entries);
}
